import { Link } from 'react-router-dom'
import { addressProblems } from '../utils/locations'
import { fieldInputStyle, fieldLabelStyle } from './AuthCardLayout'

export interface ProfileDetails {
  phone: string
  street: string
  city: string
  stateCode: string
  zip: string
  primaryUse: string
  acceptTerms: boolean
}

export const emptyProfileDetails: ProfileDetails = { phone: '', street: '', city: '', stateCode: '', zip: '', primaryUse: 'both', acceptTerms: false }

// What sign-up asks for after the person is verified, whichever way they signed up: phone, a
// physical address, and how they plan to use the site. Email sign-up and the Google/Facebook
// "complete your profile" step both render this, so they cannot collect different things.
export function validateProfileDetails(d: ProfileDetails): string | null {
  if (!d.acceptTerms) return 'Please agree to the Terms of Use, Privacy Policy and Acceptable Use Policy to continue.'
  if (d.phone && !/^\d{10}$/.test(d.phone.replace(/\D/g, ''))) return 'Phone number must be 10 digits'
  const problems = addressProblems({ address: d.street, city: d.city, state: d.stateCode, zip: d.zip })
  return problems.length > 0 ? `Please add your address: it needs ${problems.join(', ')}.` : null
}

// The fields as the API takes them.
export const profileDetailsPayload = (d: ProfileDetails) => ({
  phone: d.phone,
  primaryUse: d.primaryUse,
  acceptTerms: d.acceptTerms,
  address: d.street.trim(),
  city: d.city.trim(),
  state: d.stateCode.trim(),
  zip: d.zip.trim(),
})

const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 10)
  if (digits.length > 6) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  if (digits.length > 3) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return digits
}

export default function ProfileDetailsFields({ values, onChange }: { values: ProfileDetails; onChange: (patch: Partial<ProfileDetails>) => void }) {
  return (
    <>
      <div>
        <label htmlFor="phone" style={fieldLabelStyle}>Phone</label>
        <input id="phone" type="tel" value={values.phone} onChange={(e) => onChange({ phone: formatPhone(e.target.value) })} style={fieldInputStyle} placeholder="(555) 123-4567" />
        <p style={{ fontSize: '0.75rem', color: 'var(--slate-light)', marginTop: '0.25rem', marginBottom: 0 }}>US/Canada numbers only during beta.</p>
      </div>

      <div>
        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 0.25rem' }}>Your address</p>
        <p style={{ fontSize: '0.75rem', color: 'var(--slate-light)', margin: 0 }}>Required. Owners hand items over from it and renters receive shipments at it. It stays private until a rental needs it, and you can add more later in your profile.</p>
      </div>
      <div>
        <label htmlFor="street" style={fieldLabelStyle}>Street address</label>
        <input id="street" type="text" value={values.street} onChange={(e) => onChange({ street: e.target.value })} style={fieldInputStyle} placeholder="123 Main St" autoComplete="street-address" required />
      </div>
      <div>
        <label htmlFor="city" style={fieldLabelStyle}>City</label>
        <input id="city" type="text" value={values.city} onChange={(e) => onChange({ city: e.target.value })} style={fieldInputStyle} placeholder="Anchorage" autoComplete="address-level2" required />
      </div>
      <div>
        <label htmlFor="state" style={fieldLabelStyle}>State</label>
        <input id="state" type="text" value={values.stateCode} onChange={(e) => onChange({ stateCode: e.target.value })} style={fieldInputStyle} placeholder="AK" autoComplete="address-level1" required />
      </div>
      <div>
        <label htmlFor="zip" style={fieldLabelStyle}>ZIP code</label>
        <input id="zip" type="text" value={values.zip} onChange={(e) => onChange({ zip: e.target.value })} style={fieldInputStyle} placeholder="99501" autoComplete="postal-code" required />
      </div>

      <div>
        <label htmlFor="primaryUse" style={fieldLabelStyle}>How will you mainly use igotthatus.com?</label>
        <select id="primaryUse" value={values.primaryUse} onChange={(e) => onChange({ primaryUse: e.target.value })} style={{ ...fieldInputStyle, background: '#fff' }}>
          <option value="renter">I want to rent items from others</option>
          <option value="owner">I want to list my items for rent</option>
          <option value="both">Both — rent items and list my own</option>
        </select>
      </div>

      <label htmlFor="acceptTerms" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', fontSize: '0.875rem', color: 'var(--text-secondary)', minHeight: '44px', cursor: 'pointer' }}>
        <input id="acceptTerms" type="checkbox" checked={values.acceptTerms} onChange={(e) => onChange({ acceptTerms: e.target.checked })} style={{ marginTop: '0.2rem', width: '1.1rem', height: '1.1rem', flexShrink: 0 }} required />
        <span>
          I am 18 or older and I agree to the{' '}
          <Link to="/terms" target="_blank" style={{ color: 'var(--forest-green)' }}>Terms of Use</Link>,{' '}
          <Link to="/privacy" target="_blank" style={{ color: 'var(--forest-green)' }}>Privacy Policy</Link> and{' '}
          <Link to="/acceptable-use" target="_blank" style={{ color: 'var(--forest-green)' }}>Acceptable Use Policy</Link>, including{' '}
          that disputes with igotthatus.com are settled by individual arbitration, not in court or as a class action.
        </span>
      </label>
    </>
  )
}
